"use client";

import { useEffect } from "react";

import { usePreferences } from "@/app/hooks/usePreferences";
import { useThemeContext } from "@/app/providers/ThemeProvider";
import { trackEngagement } from "@/lib/analytics";
import type { ThemeMode } from "@/lib/types";

export function useTheme() {
  const { theme: currentTheme, setTheme } = useThemeContext();
  const { preferences, updatePreferences } = usePreferences();

  const theme = (currentTheme || "system") as ThemeMode;

  // Load saved theme preference on mount
  useEffect(() => {
    const savedTheme = preferences.theme;

    if (savedTheme && savedTheme !== currentTheme) {
      setTheme(savedTheme);
    }
  }, [preferences.theme, currentTheme, setTheme]);

  const toggleTheme = () => {
    const themeOrder: ThemeMode[] = ["system", "light", "dark"];
    const currentIndex = themeOrder.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeOrder.length;
    const newTheme = themeOrder[nextIndex];

    setTheme(newTheme);

    // Track theme change
    trackEngagement("theme_changed", { theme: newTheme });

    // Update preferences (will sync to server if authenticated)
    updatePreferences({ theme: newTheme });
  };

  return { theme, toggleTheme };
}
